/**
 * LOS (eastview-os) HTTP client.
 *
 * One axios instance for the first-party LOS backend that serves auth
 * (`/auth/*`), the pipeline (`/underwriting/*`, `/loportal/*`), and checklists
 * (`/checklists/*`) — the contract documented in `loan_and_auth_endpoints.md`.
 *
 * Auth model (loan_and_auth_endpoints.md §2/§11): every login path sets an
 * HttpOnly `los_session` cookie AND returns an `access_token` in the body. So we
 * send BOTH credentials on every call:
 *   - `withCredentials: true`  → the browser attaches the `los_session` cookie.
 *   - `Authorization: Bearer …` → from the shared token store, when present.
 * The backend resolves + scopes the user server-side; the frontend never passes
 * a user/email filter.
 *
 * The token store + the unauthorized handler are shared with `digitalSizerHttp`
 * so the app has a single session and a single logout bounce, regardless of
 * which backend a given request hit.
 *
 * Note on status codes (§11): a 403 means "missing/invalid/revoked token" OR
 * "authenticated but not entitled to *this* loan" — the two are indistinguishable
 * at the transport layer, so we do NOT blanket-logout on 403 here (that would
 * bounce a user out for opening a deal they simply can't see). Resource-level
 * 403s are mapped to domain errors at the call site (see `losPipelineApi.getLoan`
 * → `AccessDeniedError`); session loss surfaces as a 401 below.
 */
import axios from "axios";

import {
  DIGITAL_SIZER_BASE_URL,
  getAuthToken,
  setAuthToken,
  notifyUnauthorized,
} from "./digitalSizerHttp";

// The LOS base is a plain (non-secret) URL. Prefer an env override; fall back to
// the deployed eastview-os backend (loan_and_auth_endpoints.md §1). Shares the
// single base constant with `digitalSizerHttp` so there is one source of truth.
export const LOS_API_BASE = process.env.REACT_APP_API_BASE || DIGITAL_SIZER_BASE_URL;

// NOTE: do NOT set `withCredentials: true`. The backend answers CORS with a
// wildcard `Access-Control-Allow-Origin: *` (loan_and_auth_endpoints.md §1), and
// the CORS spec forbids pairing a wildcard origin with a credentialed request —
// the browser blocks it ("CORS error"). The API authorizes off the Bearer header
// (§2: "the rest of the API reads the Authorization header"), which the request
// interceptor below always attaches, so the los_session cookie isn't needed.
export const losHttp = axios.create({
  baseURL: LOS_API_BASE,
});

// ── Styled console logging ────────────────────────────────────────────────
// Mirrors the `useAxiosHook` request/response logging so LOS calls (the
// pipeline, single-loan reads, mutations) show the same collapsed green/blue
// "METHOD |>| name" / "METHOD |<| name" groups in the console. Pass an optional
// `logName` on the axios config for a friendly label; otherwise the request
// path is used.
const LOG_STYLES = {
  GET: {
    out: "background:#0b8f1a22;color:#0b8f1a;font-weight:600;padding:2px 6px;border-radius:4px;",
    in: "background:#0b8f1a44;color:#0b8f1a;font-weight:600;padding:2px 6px;border-radius:4px;",
    err: "background:#ff000022;color:#b00000;font-weight:600;padding:2px 6px;border-radius:4px;",
  },
  POST: {
    out: "background:#0a66ff22;color:#0a66ff;font-weight:600;padding:2px 6px;border-radius:4px;",
    in: "background:#0a66ff44;color:#0a66ff;font-weight:600;padding:2px 6px;border-radius:4px;",
    err: "background:#ff000022;color:#b00000;font-weight:600;padding:2px 6px;border-radius:4px;",
  },
};

const logMethod = (config) => (config?.method || "get").toUpperCase();
const logStyles = (config) => LOG_STYLES[logMethod(config)] || LOG_STYLES.GET;
const logLabel = (config) => config?.logName || config?.url || "(unknown path)";

// Per-call opt-out: pass `noLog: true` on the axios config to keep a request
// (and its response / error) out of the styled console groups. Used for noisy,
// low-signal traffic like the loan-chat send/poll.
const logSuppressed = (config) => Boolean(config?.noLog);

const logLosRequest = (config) => {
  if (logSuppressed(config)) return;
  const m = logMethod(config);
  console.groupCollapsed(`%c${m} |>| ${logLabel(config)}`, logStyles(config).out);
  console.log("URL:", (config?.baseURL || "") + (config?.url || ""));
  if (config?.data !== undefined) console.log("Payload:", config.data);
  console.groupEnd();
};

const logLosResponse = (res) => {
  if (logSuppressed(res?.config)) return;
  const m = logMethod(res?.config);
  console.groupCollapsed(
    `%c${m} |<| ${logLabel(res?.config)}`,
    logStyles(res?.config).in,
  );
  console.log("Response:", res?.data);
  console.groupEnd();
};

const logLosError = (err) => {
  const config = err?.config;
  if (logSuppressed(config)) return;
  console.groupCollapsed(
    `%c${logMethod(config)} |<| ${logLabel(config)} (ERROR)`,
    logStyles(config).err,
  );
  console.log("Status:", err?.response?.status);
  console.error(err);
  console.groupEnd();
};

losHttp.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  logLosRequest(config);
  return config;
});

// Auth 401 / transient handling — see eastview-os
// frontend_documentation/auth-login-and-password-setup.md ("DO NOT sign users
// out on transient failures"). The server session is durable (Mongo-backed, 48h,
// survives restarts) so a deploy NEVER invalidates it. What used to kill the
// session was the client treating a transient failure as "logged out". Rules:
//   • network / timeout / 5xx → transient (deploy window, scale event). Never
//     clear tokens, never sign out — let the caller retry.
//   • any API 401 → refresh ONCE with the current (possibly expired) token via
//     POST /auth/refresh (single-flight), then replay the original request.
//   • sign out ONLY when /auth/refresh itself returns HTTP 401 — the single
//     authoritative "your session is dead" signal. A refresh that dies on a
//     network error / 5xx is NOT a dead session; keep the token.
let refreshPromise = null;

const isRefreshCall = (config) => /\/auth\/refresh/.test(config?.url || "");

// Single-flight refresh → resolves to the new token (or null), or rejects with
// the refresh error (so the caller can tell a 401 from a transient failure).
const runRefresh = () => {
  if (!refreshPromise) {
    refreshPromise = losHttp
      .post("/auth/refresh", {})
      .then((r) => r?.data?.access_token ?? r?.data?.session_token ?? null)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

losHttp.interceptors.response.use(
  (res) => {
    logLosResponse(res);
    return res;
  },
  async (err) => {
    const status = err?.response?.status;
    const original = err?.config;

    // Transient: no response (network/timeout) or 5xx. Never sign out.
    if (!err?.response || status >= 500) {
      logLosError(err);
      return Promise.reject(err);
    }

    // Any 401 (except on the refresh call itself) → refresh once, then replay.
    if (
      status === 401 &&
      original &&
      !original._retriedRefresh &&
      getAuthToken() &&
      !isRefreshCall(original)
    ) {
      original._retriedRefresh = true;
      try {
        const next = await runRefresh();
        if (next) {
          setAuthToken(next);
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${next}`;
          return losHttp(original); // retry once with the fresh token
        }
        // Refresh returned no token but didn't reject → treat as transient.
      } catch (refreshErr) {
        // Dead session ONLY when /auth/refresh returns HTTP 401. Network/5xx
        // refresh failures are transient — keep the token, don't sign out.
        if (refreshErr?.response?.status === 401) notifyUnauthorized();
      }
      logLosError(err);
      return Promise.reject(err);
    }

    logLosError(err);
    return Promise.reject(err);
  },
);

export default losHttp;
